import type { WaypointGeometry } from './schemas'

export interface RingBBox {
  minLat: number
  maxLat: number
  minLng: number
  maxLng: number
}

/** GeoJSON positions are [lng, lat] — this keeps the trap visible in one place. */
export type Position = [lng: number, lat: number]

function polygonRings(geometry: WaypointGeometry): Position[][] {
  return geometry.type === 'Polygon' ? geometry.coordinates : geometry.coordinates.flat(1)
}

export function ringsOf(geometry: WaypointGeometry): Position[][] {
  return polygonRings(geometry)
}

export function bboxOf(geometry: WaypointGeometry): RingBBox {
  let minLat = Infinity
  let maxLat = -Infinity
  let minLng = Infinity
  let maxLng = -Infinity
  let seen = false
  for (const ring of polygonRings(geometry)) {
    for (const [lng, lat] of ring) {
      seen = true
      if (lat < minLat) minLat = lat
      if (lat > maxLat) maxLat = lat
      if (lng < minLng) minLng = lng
      if (lng > maxLng) maxLng = lng
    }
  }
  if (!seen) {
    throw new RangeError('waypoint geometry contains no positions')
  }
  return { minLat, maxLat, minLng, maxLng }
}

export function inBBox(lat: number, lng: number, bbox: RingBBox): boolean {
  return lat >= bbox.minLat && lat <= bbox.maxLat && lng >= bbox.minLng && lng <= bbox.maxLng
}

/**
 * Centroid of the exterior ring of the largest polygon, via the shoelace signed
 * area centroid. Good enough for labeling; not for measurements.
 */
export function centroidOf(geometry: WaypointGeometry): Position {
  const polygons = geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates
  let best: Position[] | null = null
  let bestArea = 0
  for (const rings of polygons) {
    const exterior = rings[0]
    if (!exterior || exterior.length < 3) continue
    const area = Math.abs(ringSignedArea(exterior))
    if (area >= bestArea) {
      bestArea = area
      best = exterior
    }
  }
  if (best) return ringCentroid(best)

  // Degenerate geometry (no ring with >= 3 vertices): average every position.
  let x = 0
  let y = 0
  let count = 0
  for (const ring of polygonRings(geometry)) {
    for (const [lng, lat] of ring) {
      x += lng
      y += lat
      count += 1
    }
  }
  if (count === 0) throw new RangeError('waypoint geometry contains no positions')
  return [x / count, y / count]
}

/** Assumes a closed ring (last vertex equals the first), per the GeoJSON spec. */
function ringSignedArea(ring: Position[]): number {
  let sum = 0
  let prev: Position | null = null
  for (const cur of ring) {
    if (prev) sum += prev[0] * cur[1] - cur[0] * prev[1]
    prev = cur
  }
  return sum / 2
}

function ringCentroid(ring: Position[]): Position {
  // GeoJSON rings repeat the first vertex at the end; drop it so vertex
  // averages (the degenerate-ring fallback) don't double-count it.
  const vertices =
    ring.length > 1 && ring[0]![0] === ring[ring.length - 1]![0] && ring[0]![1] === ring[ring.length - 1]![1]
      ? ring.slice(0, -1)
      : ring
  const area = ringSignedArea(ring)
  if (area === 0) {
    let x = 0
    let y = 0
    for (const [lng, lat] of vertices) {
      x += lng
      y += lat
    }
    return [x / vertices.length, y / vertices.length]
  }
  let x = 0
  let y = 0
  let prev: Position | null = null
  for (const cur of ring) {
    if (prev) {
      const cross = prev[0] * cur[1] - cur[0] * prev[1]
      x += (prev[0] + cur[0]) * cross
      y += (prev[1] + cur[1]) * cross
    }
    prev = cur
  }
  return [x / (6 * area), y / (6 * area)]
}

/** "37.7749° N 122.4194° W" — the editorial coordinate format used across the UI. */
export function formatCoordinate(lat: number, lng: number, digits = 4): string {
  const ns = lat >= 0 ? 'N' : 'S'
  const ew = lng >= 0 ? 'E' : 'W'
  return `${Math.abs(lat).toFixed(digits)}° ${ns} ${Math.abs(lng).toFixed(digits)}° ${ew}`
}

export function bboxQueryString(
  bbox: RingBBox | null,
  extra?: Record<string, string | number | boolean | undefined>,
): string {
  const params = new URLSearchParams()
  if (bbox) {
    params.set('min_lat', bbox.minLat.toFixed(6))
    params.set('max_lat', bbox.maxLat.toFixed(6))
    params.set('min_lng', bbox.minLng.toFixed(6))
    params.set('max_lng', bbox.maxLng.toFixed(6))
  }
  if (extra) {
    for (const [key, value] of Object.entries(extra)) {
      if (value !== undefined) params.set(key, String(value))
    }
  }
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}
